import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, WOOD, cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { boxClad, pitchedRoof, seams, type Era2Material } from './era2.ts';
import {
  EMBER, FORGE, GAP, IRON_ROOF, PLANK, PLANK_DK, PLINTH, Y0, box, crate, fieldstones, quoinOf, quoins, rubbleColors, wallOf, yard, type LandOpts,
} from './land.ts';
import {
  IRON_BAND, IRON_PLATE, IRON_RIVET, ballistaOn, crack, crackRound, crewman, plate, roundClad, rubbleAround, snapTop, splinters, stain, type Wear,
} from './structures.ts';
import { begin, done, part, type Model } from './types.ts';

/*
 * The pieces the eras bring on terraced land (docs/OBRAS.md §3): the foundry (era II, three cells by the rock),
 * the arsenal (era III, three cells), the lightning rod and the gate tower (era IV, one cell each) and the Titan's
 * Eye (era V, seven cells: a centre and its ring). Like the gathering post they draw their three materials (1
 * timber and fieldstone, 2 dressed stone, 3 stone bound in iron), their wear (1 worn, 2 crumbling) and their ruin.
 *
 * Performance (docs/OBRAS.md §10, docs/RENDIMIENTO.md): only geometry over the shared paints. Every lit part uses a
 * self-lit paint some other model already has (materials.ts caches by parameters, so `emis(...)` with the same
 * arguments is that very material), no see-through or two-sided paint, no lights: glows are sprites the bake turns
 * into the glow layer.
 */

const TAU = Math.PI * 2;

/** The Vigil's pale silver-white rune light (upgrades.ts LEVEL_MATS.vigil, era IV). */
const PALE = emis('#f2f6ff', '#bcd0ff', 1.4);
/** Dragon-red rune light (upgrades.ts LEVEL_MATS.rune, the level 4 pedestal). */
const RED = emis('#ffc0a8', '#ff4f3a', 1.3);
/** Copper (era II clockwork). */
const COPPER = metal('#a4512f', { roughness: 0.4 });
/** Titan stone: the dark slate of era V, with the pale runes cut in it. */
export const TITAN = stone('#3b3f47');
export const TITAN_DK = stone('#2a2d33');
export const TITAN_LT = stone('#555a63');
const ORE = stone('#4d3f37');
const RUST = stone('#6b4330');
const SLAG = stone('#1f1c1b');
const CHARRED = std('#2b2119', { roughness: 1 });
const SMOKE = stone('#3a3634');
const BELLOWS = leather('#4a3020');

const rubbleOf = rubbleColors;

/** Cracks, stains and debris on a box body's faces: the usual wear. */
const wearBox = (g: THREE.Object3D, mat: Era2Material, wear: Wear, faces: [number, number, number, number][], r: number, wr: () => number, cx = 0, cz = -0.1): void => {
  if (wear === 0) return;
  for (const [x, y, z, ry] of faces) {
    if (mat > 1 || wr() < 0.5) crack(g, x, y, z, ry, wear === 2 ? 0.22 : 0.15, wr);
    stain(g, x + 0.04, y - 0.1, z, ry, 0.06 + wr() * 0.03, wr, '#26221f');
  }
  rubbleAround(g, wear === 2 ? 8 : 3, r, wr, rubbleOf(mat), cx, cz);
  splinters(g, wear * 2, r - 0.05, wr, cx, cz);
};

/* ---------- the foundry ---------- */

/**
 * The foundry: a bottle kiln at the back left, its tall stack banded in iron and its mouth burning, a lean-to
 * with a great leather bellows piped into it at the back right; a glowing runnel runs from the tap hole down to
 * an ingot mould at the point of the yard, a heap of rusty ore waits on the left and finished bars are stacked on
 * the right. Fieldstone and timber in wood, dressed stone in stone, plated with an iron roof in iron. Worn (1):
 * cracks and soot. Crumbling (2): the stack snapped, the lean-to's roof holed, the fire low.
 */
export const foundry = (o?: LandOpts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.85, 161);
  const { g } = c;
  const wr = mulberry32(1610 + mat * 3 + wear);
  const iron = mat === 3;
  const wallM = wallOf(mat);
  yard(g, mat);

  // The bottle kiln.
  const kx = -0.3, kz = -0.3;
  M(new THREE.CylinderGeometry(0.31, 0.35, 0.36, 10), wallM, g).position.set(kx, Y0 + 0.18, kz);
  M(new THREE.CylinderGeometry(0.15, 0.31, 0.36, 10), wallM, g).position.set(kx, Y0 + 0.54, kz);
  for (const y of [Y0 + 0.1, Y0 + 0.3]) ring(g, 0.34 - (y - Y0) * 0.11, 0.016, IRON_BAND, y, 4, 20).position.set(kx, y, kz);
  if (mat === 1) fieldstones(g, 6, kx - 0.2, kx + 0.2, Y0 + 0.05, Y0 + 0.3, kz + 0.33, wr);
  // The stack.
  const stackTop = wear === 2 ? 1.05 : 1.55;
  const sh = stackTop - (Y0 + 0.72);
  M(new THREE.CylinderGeometry(0.1, 0.13, sh, 8), wallM, g).position.set(kx, Y0 + 0.72 + sh / 2, kz);
  for (const y of [0.95, 1.25]) if (y < stackTop - 0.05) ring(g, 0.115, 0.014, IRON_BAND, y, 4, 12).position.set(kx, y, kz);
  if (wear === 2) {
    snapTop(g, kx, stackTop, kz, 0.07, wallM, wr);
    const fallen = M(new THREE.CylinderGeometry(0.1, 0.11, 0.36, 8), wallM, g);
    fallen.position.set(-0.68, Y0 + 0.1, 0.0);
    fallen.rotation.set(0.2, 0.7, Math.PI / 2);
  } else {
    M(new THREE.CylinderGeometry(0.13, 0.12, 0.05, 8), quoinOf(mat), g).position.set(kx, stackTop, kz);
    M(new THREE.CylinderGeometry(0.07, 0.07, 0.012, 8), EMBER, g).position.set(kx, stackTop + 0.022, kz);
    // A plume of soot leaning off the stack.
    for (const [dx, dy, s] of [[0.03, 0.12, 0.07], [0.08, 0.24, 0.09], [0.15, 0.37, 0.1]] as const) {
      const p = M(new THREE.DodecahedronGeometry(s, 0), SMOKE, g);
      p.position.set(kx + dx, stackTop + dy, kz + dx * 0.4);
    }
  }
  // The kiln's mouth toward the field: an arch on fire.
  const mz = kz + 0.335;
  box(g, BLACK, 0.18, 0.16, 0.03, kx, Y0 + 0.09, mz);
  box(g, FORGE, 0.14, 0.07, 0.012, kx, Y0 + 0.05, mz + 0.012);
  box(g, EMBER, 0.15, 0.08, 0.01, kx, Y0 + 0.12, mz + 0.008);
  const arch = M(new THREE.TorusGeometry(0.1, 0.025, 4, 8, Math.PI), quoinOf(mat), g);
  arch.position.set(kx, Y0 + 0.16, mz + 0.012);
  const fire = [1, 0.75, 0.4][wear] as number;
  glowSprite(g, '#ff7a2a', 0.55, V(kx, Y0 + 0.12, mz + 0.08), 0.65 * fire);

  // The runnel from the tap hole to the ingot mould, glowing.
  const run: [number, number][] = [[kx + 0.04, mz + 0.06], [-0.12, 0.18], [0.02, 0.36]];
  for (let i = 0; i < run.length - 1; i++) {
    const [x0, z0] = run[i] as [number, number];
    const [x1, z1] = run[i + 1] as [number, number];
    const len = Math.hypot(x1 - x0, z1 - z0);
    const ry = Math.atan2(x1 - x0, z1 - z0);
    box(g, PLINTH, 0.07, 0.02, len + 0.02, (x0 + x1) / 2, Y0 + 0.01, (z0 + z1) / 2, ry);
    if (wear < 2 || i === 0) box(g, FORGE, 0.03, 0.012, len, (x0 + x1) / 2, Y0 + 0.018, (z0 + z1) / 2, ry);
  }
  const mould = G(g, 0.06, Y0, 0.44);
  mould.rotation.y = 0.5;
  box(mould, DSTEEL, 0.26, 0.05, 0.14, 0, 0.025, 0);
  for (const x of [-0.07, 0.07]) box(mould, wear === 2 ? SLAG : FORGE, 0.09, 0.012, 0.08, x, 0.052, 0);
  glowSprite(g, '#ff8a3a', 0.4, V(0.02, Y0 + 0.08, 0.36), 0.5 * fire);

  // The bellows lean-to at the back right.
  const bx = 0.36, bz = -0.36, bw = 0.5, bd = 0.34, bh = 0.4;
  const postM = iron ? IRON_BAND : DWOOD;
  box(g, mat === 1 ? WOOD : wallM, bw, bh, 0.05, bx, Y0 + bh / 2, bz - bd / 2 + 0.025);
  for (const k of [-1, 1]) box(g, postM, 0.045, bh - 0.06, 0.045, bx + k * (bw / 2 - 0.03), Y0 + (bh - 0.06) / 2, bz + bd / 2 - 0.03);
  const roof = G(g, bx, Y0 + bh - 0.02, bz);
  roof.rotation.x = 0.28;
  box(roof, iron ? IRON_ROOF : PLANK, bw + 0.1, 0.03, bd + 0.12, 0, 0, 0);
  if (!iron) for (let x = -bw / 2 + 0.05; x < bw / 2; x += 0.1) box(roof, PLANK_DK, 0.012, 0.012, bd + 0.12, x, 0.02, 0);
  else for (let x = -bw / 2; x <= bw / 2 + 0.01; x += 0.12) box(roof, IRON_BAND, 0.014, 0.02, bd + 0.12, x, 0.022, 0);
  if (wear === 2) box(roof, GAP, 0.16, 0.012, 0.14, 0.08, 0.022, 0.02);
  // The bellows: two boards and the leather between, its nozzle in an iron pipe to the kiln.
  const bel = G(g, bx + 0.02, Y0 + 0.12, bz - 0.02);
  bel.rotation.y = Math.PI / 2 + 0.2;
  box(bel, WOOD, 0.3, 0.025, 0.18, 0, 0.0, 0);
  const top = box(bel, WOOD, 0.3, 0.025, 0.18, 0.02, 0.1, 0);
  top.rotation.z = -0.18;
  box(bel, BELLOWS, 0.24, 0.08, 0.16, 0.03, 0.05, 0).rotation.z = -0.09;
  limb(bel, V(0.16, 0.05, 0), V(0.26, 0.05, 0), 0.025, 0.012, DSTEEL, 5);
  limb(g, V(bx - 0.14, Y0 + 0.17, bz - 0.02), V(kx + 0.3, Y0 + 0.2, kz), 0.018, 0.018, DSTEEL, 5);
  limb(bel, V(-0.12, 0.13, 0), V(-0.2, 0.3, 0), 0.008, 0.008, DWOOD, 3);

  // The ore heap, left: rusty lumps.
  for (let i = 0; i < 7; i++) {
    const r = M(new THREE.DodecahedronGeometry(0.045 + wr() * 0.03, 0), i % 3 === 0 ? RUST : ORE, g);
    r.position.set(-0.52 + (wr() - 0.5) * 0.22, Y0 + 0.04 + (i > 4 ? 0.06 : 0), 0.14 + (wr() - 0.5) * 0.18);
    r.rotation.set(wr(), wr() * 3, wr());
  }
  // A barrow by the heap.
  const bw2 = G(g, -0.36, Y0, 0.34);
  bw2.rotation.y = -0.6;
  box(bw2, WOOD, 0.16, 0.06, 0.12, 0, 0.09, 0);
  for (const k of [-1, 1]) limb(bw2, V(-0.06, 0.08, k * 0.05), V(-0.22, 0.06, k * 0.06), 0.008, 0.008, DWOOD, 3);
  const wh = M(new THREE.TorusGeometry(0.045, 0.01, 4, 10), DWOOD, bw2);
  wh.position.set(0.09, 0.05, 0);
  for (let i = 0; i < 3; i++) M(new THREE.DodecahedronGeometry(0.035, 0), ORE, bw2).position.set(-0.03 + i * 0.035, 0.13, (i - 1) * 0.02);

  // The stacked bars, right: three courses, crosswise.
  const ix = 0.4, iz = 0.16;
  for (let l = 0; l < 3; l++) {
    const n = 3 - l;
    for (let i = 0; i < n; i++) {
      const along = l % 2 === 0;
      const o2 = (i - (n - 1) / 2) * 0.055;
      box(g, l === 2 ? IRON_PLATE : DSTEEL, along ? 0.16 : 0.045, 0.03, along ? 0.045 : 0.16, ix + (along ? 0 : o2), Y0 + 0.015 + l * 0.03, iz + (along ? o2 : 0));
    }
  }
  crate(g, 0.12, 0.58, Y0, -0.05, 0.3);

  if (iron) {
    boxClad(g, bx, bz, bw, bd, Y0, Y0 + bh, [Y0 + 0.2], Y0 + 0.18, ['b', 'r']);
    for (const a of [0.6, -0.6]) plate(g, kx + Math.sin(a) * 0.335, Y0 + 0.18, kz + Math.cos(a) * 0.335, 0.14, 0.18, a, true, -0.1);
  } else if (mat === 2) quoins(g, quoinOf(mat), bx, bz - bd / 2 + 0.025, bw, 0.05, Y0, Y0 + bh - 0.04);

  wearBox(g, mat, wear, [[kx + 0.1, Y0 + 0.35, kz + 0.32, 0.3], [bx, Y0 + 0.28, bz - bd / 2, Math.PI], [kx - 0.33, Y0 + 0.25, kz, -Math.PI / 2]], 0.62, wr);
  return done(c, wear === 2 ? 1.15 : 1.6);
};

/* ---------- the arsenal ---------- */

/**
 * The arsenal: a long armoury shed across the back of the yard, its doors open on a dark store and two crossed
 * bolts on a crimson hanging over them; in front, a timber rack of ballista bolts on the left, a pyramid of
 * catapult stones on the right and casks and a crate at the point. Plank and fieldstone in wood, dressed stone
 * in stone, plated with an iron roof in iron. Worn (1): cracks, soot, gaps in the roof. Crumbling (2): the roof
 * holed, the rack knocked over, the pyramid spilled.
 */
export const arsenal = (o?: LandOpts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.85, 171);
  const { g } = c;
  const wr = mulberry32(1710 + mat * 3 + wear);
  const iron = mat === 3;
  const wallM = wallOf(mat);
  yard(g, mat);

  const W = 1.0, D = 0.4, Z = -0.38, H = 0.42;
  const front = Z + D / 2;
  box(g, wallM, W, H, D, 0, Y0 + H / 2, Z);
  if (mat === 1) {
    fieldstones(g, 10, -W / 2 + 0.05, W / 2 - 0.05, Y0 + 0.04, Y0 + H - 0.06, front + 0.005, wr);
    box(g, DWOOD, W + 0.03, 0.04, D + 0.03, 0, Y0 + H - 0.02, Z);
  } else quoins(g, quoinOf(mat), 0, Z, W, D, Y0, Y0 + H);
  const roofM = iron ? IRON_ROOF : mat === 1 ? PLANK : stone('#3e3a36');
  const span = D + 0.04, rh = 0.24, len = W + 0.04;
  const roof = pitchedRoof(g, 0, Y0 + H, Z, span, rh, len, roofM, wallM);
  if (iron) seams(roof.grp, span, rh, len + 0.06, roof.angle);
  for (let i = 0; i < wear * 2; i++) {
    const side = i % 2 ? -1 : 1;
    const k = 0.3 + wr() * 0.45;
    const gap = box(roof.grp, GAP, 0.09 + wr() * 0.08, 0.01, 0.07, (wr() - 0.5) * 0.7, rh * (1 - k) + 0.04, side * (span / 2) * k);
    gap.rotation.x = side * roof.angle;
  }
  // Open doors on a dark store, bolts and a stone glimpsed inside.
  box(g, BLACK, 0.34, 0.3, 0.012, 0, Y0 + 0.15, front + 0.003);
  for (const k of [-1, 1]) {
    const leaf = box(g, DWOOD, 0.16, 0.29, 0.02, k * 0.25, Y0 + 0.145, front + 0.07, k * 1.2);
    for (const y of [-0.08, 0.08]) box(leaf, iron ? IRON_BAND : DSTEEL, 0.16, 0.02, 0.006, 0, y, 0.012);
  }
  box(g, mat === 1 ? DWOOD : quoinOf(mat), 0.42, 0.05, 0.05, 0, Y0 + 0.32, front + 0.012);
  for (let i = 0; i < 4; i++) limb(g, V(-0.1 + i * 0.05, Y0 + 0.01, front - 0.04), V(-0.08 + i * 0.05, Y0 + 0.24, front - 0.02), 0.008, 0.008, WOOD, 3);
  // A crimson hanging over the doors with two crossed bolts.
  box(g, cloth('#7a1d22'), 0.16, 0.13, 0.012, 0, Y0 + H - 0.04, front + 0.02);
  for (const k of [-1, 1]) box(g, DSTEEL, 0.012, 0.13, 0.008, 0, Y0 + H - 0.04, front + 0.03).rotation.z = k * 0.7;

  // The bolt rack, left: an A-frame stand, its bolts leaning on the rail, heads up.
  const rk = G(g, -0.44, Y0, 0.08);
  rk.rotation.y = 0.35;
  if (wear === 2) {
    rk.rotation.z = Math.PI / 2 - 0.15;
    rk.position.set(-0.42, Y0 + 0.04, 0.12);
  }
  for (const k of [-1, 1]) {
    limb(rk, V(k * 0.2, 0, -0.08), V(k * 0.2, 0.34, 0), 0.016, 0.014, DWOOD, 4);
    limb(rk, V(k * 0.2, 0, 0.08), V(k * 0.2, 0.34, 0), 0.016, 0.014, DWOOD, 4);
  }
  box(rk, DWOOD, 0.46, 0.025, 0.025, 0, 0.32, 0);
  for (let i = 0; i < 7; i++) {
    const x = -0.17 + i * 0.057;
    limb(rk, V(x, 0.0, 0.1), V(x, 0.46, -0.01), 0.009, 0.009, WOOD, 4);
    const tip = M(new THREE.ConeGeometry(0.018, 0.06, 4), DSTEEL, rk);
    tip.position.set(x, 0.49, -0.018);
    tip.rotation.x = -0.24;
  }
  if (wear === 2) {
    for (let i = 0; i < 3; i++) {
      const b = G(g, -0.3 + i * 0.08, Y0 + 0.012, 0.3 + wr() * 0.1);
      b.rotation.y = 0.4 + wr();
      M(new THREE.CylinderGeometry(0.009, 0.009, 0.46, 4), WOOD, b).rotation.x = Math.PI / 2;
    }
  }

  // The pyramid of stones, right: four, three, one.
  const px = 0.42, pz = 0.08;
  const stoneM = stone('#6d6559');
  const spots: [number, number, number][] = [
    [-0.065, 0, -0.065], [0.065, 0, -0.065], [-0.065, 0, 0.065], [0.065, 0, 0.065],
    [0, 1, -0.035], [-0.03, 1, 0.05], [0.055, 1, 0.04], [0.01, 2, 0.01],
  ];
  spots.forEach(([x, l, z], i) => {
    if (wear === 2 && l > 0) {
      M(new THREE.DodecahedronGeometry(0.065, 0), stoneM, g).position.set(px + 0.12 + i * 0.03, Y0 + 0.06, pz + 0.18 + (i % 2) * 0.08);
      return;
    }
    const s = M(new THREE.DodecahedronGeometry(0.068, 0), stoneM, g);
    s.position.set(px + x, Y0 + 0.065 + l * 0.1, pz + z);
    s.rotation.set(i, i * 0.7, 0);
  });
  // A timber kerb round the stones.
  for (const k of [-1, 1]) box(g, DWOOD, 0.3, 0.035, 0.03, px, Y0 + 0.018, pz + k * 0.15);

  // Casks and a crate at the point; a crewman carries a bolt in.
  for (const [x, z] of [[-0.08, 0.42], [0.06, 0.48]] as const) {
    const cask = M(new THREE.CylinderGeometry(0.055, 0.055, 0.14, 9), WOOD, g);
    cask.position.set(x, Y0 + 0.07, z);
    for (const y of [0.035, 0.105]) ring(g, 0.057, 0.006, DSTEEL, Y0 + y, 3, 12).position.set(x, Y0 + y, z);
  }
  crate(g, 0.13, 0.18, Y0, 0.36, 0.5);
  crewman(g, -0.2, Y0, 0.26, Math.PI + 0.5, 'carry', 0.42, '#6e1f24');

  if (iron) {
    boxClad(g, 0, Z, W, D, Y0, Y0 + H, [Y0 + 0.2, Y0 + H - 0.04], Y0 + 0.2, ['l', 'b', 'r']);
    for (const x of [-W / 2 + 0.08, W / 2 - 0.08]) plate(g, x, Y0 + 0.12, front + 0.009, 0.1, 0.2, 0);
  }
  wearBox(g, mat, wear, [[-0.36, Y0 + 0.3, front + 0.004, 0], [0.36, Y0 + 0.3, front + 0.004, 0], [W / 2 + 0.004, Y0 + 0.28, Z, Math.PI / 2]], 0.64, wr);
  return done(c, 0.95);
};

/* ---------- the lightning rod ---------- */

/**
 * The lightning rod: a tall iron mast on a stepped stone base, stayed by three iron rods, a copper coil wound
 * round it between two pale insulators and a crown of iron prongs at the top where a pale spark sits. Fieldstone
 * base in wood, dressed stone in stone, plated in iron. Worn (1): cracks, a stay gone. Crumbling (2): the mast
 * bent over above the coil, the crown hanging, the spark out.
 */
export const rod = (o?: LandOpts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.62, 181);
  const { g } = c;
  const wr = mulberry32(1810 + mat * 3 + wear);
  const baseM = mat === 1 ? PLINTH : wallOf(mat);
  M(new THREE.CylinderGeometry(0.36, 0.4, 0.12, 8), baseM, g).position.y = 0.06;
  M(new THREE.CylinderGeometry(0.22, 0.29, 0.3, 8), mat === 1 ? stone('#77736c') : baseM, g).position.y = 0.27;
  M(new THREE.CylinderGeometry(0.25, 0.25, 0.05, 8), mat === 1 ? PLINTH : quoinOf(mat), g).position.y = 0.44;
  if (mat === 3) roundClad(g, 0.29, 0.22, 0.12, 0.42, 8, 0, 0.3, [0.38]);
  else if (mat === 1) for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU + 0.3;
    const s = M(new THREE.DodecahedronGeometry(0.045, 0), PLINTH, g);
    s.position.set(Math.sin(a) * 0.26, 0.2 + (i % 2) * 0.1, Math.cos(a) * 0.26);
    s.scale.z = 0.5;
    s.rotation.y = a;
  }
  // Copper ground ring and a cable into the earth.
  ring(g, 0.39, 0.012, COPPER, 0.125, 4, 16);
  limb(g, V(0.0, 0.47, 0.08), V(0.33, 0.13, 0.22), 0.008, 0.008, COPPER, 3);

  const bent = wear === 2;
  const mastTop = 2.3;
  const coilLo = 0.95, coilHi = 1.5;
  const mast = G(g, 0, 0, 0);
  limb(mast, V(0, 0.46, 0), V(0, bent ? coilHi + 0.12 : mastTop, 0), 0.04, 0.026, IRON_BAND, 6);
  // Stays to the base (worn: one gone).
  for (let i = 0; i < 3; i++) {
    if (wear >= 1 && i === 1) continue;
    const a = (i / 3) * TAU + 0.5;
    limb(g, V(Math.sin(a) * 0.3, 0.13, Math.cos(a) * 0.3), V(0, 1.15, 0), 0.007, 0.007, DSTEEL, 3);
    M(new THREE.BoxGeometry(0.05, 0.03, 0.05), IRON_RIVET, g).position.set(Math.sin(a) * 0.3, 0.135, Math.cos(a) * 0.3);
  }
  // Insulators and the coil between them.
  for (const y of [coilLo - 0.04, coilHi + 0.04]) {
    const ins = M(new THREE.CylinderGeometry(0.075, 0.075, 0.05, 8), stone('#d6d3cc'), mast);
    ins.position.y = y;
    ring(mast, 0.078, 0.008, COPPER, y, 3, 10);
  }
  const pts: THREE.Vector3[] = [];
  const turns = 6;
  for (let i = 0; i <= 72; i++) {
    const t = i / 72;
    const a = t * TAU * turns;
    pts.push(V(Math.cos(a) * 0.075, coilLo + t * (coilHi - coilLo), Math.sin(a) * 0.075));
  }
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 72, 0.013, 4), COPPER, mast);

  // The crown: a ring and four prongs round a spike, the spark at the tip.
  const crown = G(mast, 0, mastTop, 0);
  if (bent) {
    // Bent over above the coil: the upper mast hangs out to the side, the crown low.
    const upper = G(mast, 0, coilHi + 0.12, 0);
    upper.rotation.z = -1.9;
    limb(upper, V(0, 0, 0), V(0, 0.55, 0), 0.026, 0.02, IRON_BAND, 6);
    snapTop(g, 0, coilHi + 0.12, 0, 0.03, IRON_BAND, wr);
    crown.position.set(0, 0, 0);
    upper.add(crown);
    crown.position.y = 0.55;
  }
  ring(crown, 0.09, 0.01, IRON_BAND, -0.12, 4, 12);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU;
    limb(crown, V(Math.sin(a) * 0.09, -0.12, Math.cos(a) * 0.09), V(Math.sin(a) * 0.13, 0.08, Math.cos(a) * 0.13), 0.009, 0.004, IRON_BAND, 4);
  }
  limb(crown, V(0, -0.02, 0), V(0, 0.2, 0), 0.02, 0.004, IRON_RIVET, 5);
  if (!bent) {
    const spark = M(new THREE.OctahedronGeometry(0.04, 0), PALE, crown);
    spark.position.y = 0.23;
    spark.scale.y = 1.6;
    glowSprite(crown, '#dfe8ff', 0.45, V(0, 0.23, 0), wear === 1 ? 0.5 : 0.75);
  }
  // A pale rune cut in the base's front.
  const rz = 0.26 * Math.cos(Math.PI / 8) + 0.012;
  box(g, PALE, 0.018, 0.08, 0.006, 0, 0.27, rz);
  for (const k of [-1, 1]) box(g, PALE, 0.05, 0.014, 0.006, k * 0.017, 0.33, rz).rotation.z = k * -0.6;

  if (wear > 0) {
    for (let i = 0; i < wear + 1; i++) crackRound(g, 0.26, 0.32 - wr() * 0.1, 0.3 + i * 1.7 + wr(), wear === 2 ? 0.2 : 0.14, wr);
    stain(g, 0.18, 0.25, 0.2, 0.7, 0.06, wr, '#2a2622');
    rubbleAround(g, wear === 2 ? 7 : 3, 0.45, wr, rubbleOf(mat));
  }
  return done(c, bent ? 1.7 : 2.55);
};

/* ---------- the gate tower ---------- */

export interface GateTowerOpts extends LandOpts {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
}

/**
 * The gate tower: a squat round tower made to flank a gate, a crenellated crown over a ring of corbels and a
 * light ballista turning on its roof (part 'turret'), arrow slits on three sides and a low door at the back.
 * Fieldstone under a timber hoarding in wood, dressed stone in stone, iron-skirted slate in iron. It grows with
 * its level (it has no pedestal of its own: it stands at the gate): 2 an iron band, 3 a crimson hanging and a
 * second bow, 4 the steel-shod ballista with its mantlet, 5 a silver band and the Vigil's runes, 6 a foot of
 * titan stone with pale runes. Worn (1): merlons gone, cracks. Crumbling (2): half the crown down, a hole in the
 * face, a heap of stone.
 */
export const gateTower = (o?: GateTowerOpts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const level = o?.level ?? 1;
  const c = begin(o, 0.62, 191);
  const { g } = c;
  const wr = mulberry32(1910 + mat * 3 + wear + level * 7);
  const iron = mat === 3;
  const titan = level >= 6;
  const foot = titan ? 0.14 : 0;
  const body = G(g, 0, foot, 0);
  if (titan) {
    M(new THREE.CylinderGeometry(0.66, 0.72, foot, 10), TITAN, g).position.y = foot / 2;
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * TAU + 0.3;
      box(g, PALE, 0.02, 0.07, 0.006, Math.sin(a) * 0.695, foot / 2, Math.cos(a) * 0.695, a);
    }
    glowSprite(g, '#dfe8ff', 0.3, V(0.4, foot / 2, 0.55), 0.5);
  }
  const H = 0.82;
  const r0 = 0.58, r1 = 0.52;
  const st = mat === 1 ? stone('#77736c') : wallOf(mat);
  if (mat === 1) {
    // Fieldstone up to the hoarding, then a timber gallery.
    M(new THREE.CylinderGeometry(0.54, r0, 0.52, 10), st, body).position.y = 0.26;
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * TAU + 0.2;
      const s = M(new THREE.DodecahedronGeometry(0.05, 0), PLINTH, body);
      s.position.set(Math.sin(a) * 0.56, 0.12 + (i % 3) * 0.13, Math.cos(a) * 0.56);
      s.scale.z = 0.45;
      s.rotation.y = a;
    }
    M(new THREE.CylinderGeometry(0.6, 0.56, H - 0.52, 10), WOOD, body).position.y = 0.52 + (H - 0.52) / 2;
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * TAU;
      box(body, DWOOD, 0.02, H - 0.5, 0.02, Math.sin(a) * 0.6, 0.52 + (H - 0.52) / 2, Math.cos(a) * 0.6, a);
    }
  } else {
    M(new THREE.CylinderGeometry(r1, r0, H, 10), st, body).position.y = H / 2;
    const mortar = std('#3a3632');
    for (let i = 0; i < 3; i++) ring(body, r0 - 0.012 - i * 0.022, 0.006, mortar, 0.18 + i * 0.22, 3, 24);
    if (iron) roundClad(body, r0, r1, 0, H, 10, 0, 0.36, [0.5, H - 0.08]);
  }
  // Corbels under the crown.
  const cr = r1 + 0.05;
  M(new THREE.CylinderGeometry(cr, r1, 0.08, 10), mat === 1 ? DWOOD : quoinOf(mat), body).position.y = H + 0.04;
  // Merlons (worn: some gone; crumbling: half the crown down).
  const lost = wear === 2 ? [0, 1, 2, 4, 5] : wear === 1 ? [1, 6] : [];
  for (let i = 0; i < 10; i++) {
    if (lost.includes(i)) continue;
    const a = (i / 10) * TAU;
    box(body, mat === 1 ? WOOD : st, 0.15, 0.16, 0.1, Math.sin(a) * (cr - 0.05), H + 0.16, Math.cos(a) * (cr - 0.05), a);
    if (iron) M(new THREE.ConeGeometry(0.02, 0.1, 4), DSTEEL, body).position.set(Math.sin(a) * (cr - 0.05), H + 0.29, Math.cos(a) * (cr - 0.05));
  }
  M(new THREE.CylinderGeometry(cr - 0.08, cr - 0.08, 0.04, 10), mat === 1 ? DWOOD : stone('#6f6a63'), body).position.y = H + 0.1;
  // Arrow slits, a door at the back.
  for (const a of [0, 1.9, -1.9]) box(body, BLACK, 0.045, 0.2, 0.03, Math.sin(a) * (r0 - 0.06), 0.38, Math.cos(a) * (r0 - 0.06), a);
  box(body, BLACK, 0.18, 0.28, 0.03, 0, 0.14, -(r0 - 0.02), Math.PI);
  box(body, DWOOD, 0.15, 0.25, 0.02, 0, 0.13, -(r0 - 0.005), Math.PI);
  // Level marks.
  if (level >= 2) ring(body, r0 - 0.02, 0.02, DSTEEL, 0.62, 4, 24);
  if (level >= 3 && wear < 2) {
    box(body, cloth('#7a1d22'), 0.18, 0.3, 0.012, 0, 0.5, r0 - 0.02);
    box(body, cloth('#d9dce3'), 0.06, 0.06, 0.016, 0, 0.55, r0 - 0.012).rotation.z = Math.PI / 4;
  }
  if (level >= 5) {
    ring(body, r0 - 0.006, 0.016, metal('#c9d0da', { roughness: 0.3 }), 0.06, 4, 24);
    for (const a of [Math.PI / 2, -Math.PI / 2]) {
      const ap = r0 - 0.03;
      box(body, PALE, 0.025, 0.12, 0.008, Math.sin(a) * (ap + 0.012), 0.42, Math.cos(a) * (ap + 0.012), a);
      box(body, PALE, 0.08, 0.016, 0.008, Math.sin(a) * (ap + 0.012), 0.46, Math.cos(a) * (ap + 0.012), a);
      glowSprite(body, '#dfe8ff', 0.22, V(Math.sin(a) * (ap + 0.06), 0.44, Math.cos(a) * (ap + 0.06)), 0.55);
    }
  }
  // The light ballista on the roof.
  const bl = (level >= 4 ? 4 : level) as 1 | 2 | 3 | 4;
  const turret = ballistaOn(body, H + 0.32, DWOOD, iron ? DSTEEL : WOOD, bl);
  turret.scale.setScalar(0.85);

  if (wear > 0) {
    for (let i = 0; i < wear * 2 + 1; i++) crackRound(body, r0 - 0.03, 0.62 - wr() * 0.25, (i / (wear * 2 + 1)) * TAU + wr(), wear === 2 ? 0.34 : 0.22, wr);
    for (let i = 0; i < wear; i++) {
      const a = wr() * TAU;
      stain(body, Math.sin(a) * (r0 - 0.02), 0.3 + wr() * 0.3, Math.cos(a) * (r0 - 0.02), a, 0.08, wr);
    }
    if (wear === 2) {
      const a = 0.9;
      box(body, stone('#2f2b27'), 0.2, 0.15, 0.04, Math.sin(a) * (r0 - 0.03), 0.3, Math.cos(a) * (r0 - 0.03), a);
    }
    rubbleAround(g, wear === 2 ? 10 : 4, 0.62, wr, rubbleOf(mat));
    if (mat === 1) splinters(g, wear * 3, 0.6, wr);
  }
  const t = (n: number): void => {
    turret.rotation.y = Math.sin(n * 0.22) * 0.7;
  };
  return done(c, foot + H + 0.55, t);
};

/* ---------- the Titan's Eye ---------- */

/** The Eye's ring: its six plinths stand on the six cells round its centre, one hex out (world units). */
export const EYE_RING = 1.2;

/**
 * The Titan's Eye, modelled at world size (one hex between centres is 1.2): a broad dais of titan stone over its
 * centre cell, a pale rune circle and six rune lines cut in it to six carved plinths standing on the ring cells,
 * each crowned with a pale shard; in the middle, a stepped pedestal and on it the Eye itself (part 'turret',
 * turning to what it looks at): a great stone socket held in a yoke, ringed with rays, its iris a pale burning
 * ring round a dragon-red slit. Worn (1): cracks, a plinth broken. Crumbling (2): two plinths down, a ray broken
 * off, the iris cracked and dim, rubble on the dais.
 */
export const titanEye = (o?: LandOpts): Model => {
  const wear = o?.wear ?? 0;
  const c = begin(o, 1.6, 201);
  const { g } = c;
  const wr = mulberry32(2010 + wear);
  // The dais and the inner step.
  const dais = M(new THREE.CylinderGeometry(1.45, 1.55, 0.14, 12), TITAN, g);
  dais.position.y = 0.07;
  M(new THREE.CylinderGeometry(1.47, 1.47, 0.03, 12, 1, true), TITAN_DK, g).position.y = 0.125;
  ring(g, 1.28, 0.016, PALE, 0.142, 3, 48);
  M(new THREE.CylinderGeometry(0.8, 0.86, 0.14, 12), TITAN_LT, g).position.y = 0.21;
  ring(g, 0.74, 0.012, PALE, 0.282, 3, 36);
  // Six plinths on the ring cells and the rune lines to them.
  const broken = wear === 2 ? [1, 4] : wear === 1 ? [1] : [];
  for (let k = 0; k < 6; k++) {
    const a = (k * TAU) / 6;
    const sx = Math.sin(a), sz = Math.cos(a);
    box(g, PALE, 0.025, 0.006, 0.4, sx * 1.04, 0.142, sz * 1.04, a);
    const pg = G(g, sx * EYE_RING, 0.14, sz * EYE_RING);
    pg.rotation.y = a;
    box(pg, TITAN_DK, 0.36, 0.1, 0.36, 0, 0.05, 0);
    if (broken.includes(k)) {
      M(new THREE.CylinderGeometry(0.13, 0.15, 0.2, 6), TITAN, pg).position.y = 0.2;
      snapTop(pg, 0, 0.3, 0, 0.1, TITAN, wr);
      const fall = G(g, sx * (EYE_RING + 0.32), 0.2, sz * (EYE_RING + 0.32));
      fall.rotation.set(Math.PI / 2, 0, a + 0.4);
      M(new THREE.CylinderGeometry(0.11, 0.13, 0.4, 6), TITAN, fall);
      continue;
    }
    M(new THREE.CylinderGeometry(0.11, 0.15, 0.62, 6), TITAN, pg).position.y = 0.41;
    box(pg, TITAN_DK, 0.27, 0.07, 0.27, 0, 0.75, 0);
    // A pale rune on the outward face and a shard on top.
    box(pg, PALE, 0.025, 0.18, 0.008, 0, 0.42, 0.135);
    for (const s of [-1, 1]) box(pg, PALE, 0.06, 0.016, 0.008, s * 0.02, 0.5, 0.135).rotation.z = s * 0.6;
    const shard = M(new THREE.OctahedronGeometry(0.07, 0), PALE, pg);
    shard.position.y = 0.92;
    shard.scale.y = 2.2;
    shard.rotation.y = 0.4;
    if (k % 2 === 0) glowSprite(pg, '#dfe8ff', 0.4, V(0, 0.92, 0), wear === 2 ? 0.4 : 0.6);
  }
  // The pedestal.
  const ped = M(new THREE.CylinderGeometry(0.4, 0.52, 0.5, 8), TITAN, g);
  ped.position.y = 0.53;
  ped.rotation.y = Math.PI / 8;
  M(new THREE.CylinderGeometry(0.46, 0.44, 0.06, 8), TITAN_DK, g).position.y = 0.81;
  ring(g, 0.47, 0.014, PALE, 0.45, 3, 24);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU;
    const b = box(g, TITAN_DK, 0.1, 0.42, 0.12, Math.sin(a) * 0.46, 0.49, Math.cos(a) * 0.46, a);
    b.rotation.x = -0.12;
  }

  // The Eye, turning.
  const eye = part(G(g, 0, 0.84, 0), 'turret');
  M(new THREE.CylinderGeometry(0.3, 0.36, 0.1, 10), TITAN_DK, eye).position.y = 0.05;
  for (const s of [-1, 1]) {
    limb(eye, V(s * 0.24, 0.08, 0), V(s * 0.58, 0.62, 0), 0.08, 0.06, TITAN, 6);
    M(new THREE.SphereGeometry(0.075, 6, 4), TITAN_LT, eye).position.set(s * 0.6, 0.66, 0);
  }
  const socket = G(eye, 0, 0.78, 0.02);
  const frame = M(new THREE.TorusGeometry(0.5, 0.1, 6, 24), TITAN, socket);
  frame.scale.set(1, 0.72, 1);
  for (const r of [0, Math.PI]) {
    const lid = M(new THREE.TorusGeometry(0.55, 0.055, 4, 14, Math.PI), TITAN_LT, socket);
    lid.rotation.z = r;
    lid.scale.set(1, 0.78, 1);
    lid.position.z = 0.03;
  }
  // The eyeball behind the socket, the iris and the slit.
  const ball = M(new THREE.SphereGeometry(0.42, 12, 8), TITAN_DK, socket);
  ball.scale.set(1, 0.7, 0.5);
  ball.position.z = -0.04;
  const iris = M(new THREE.TorusGeometry(0.2, 0.055, 5, 24), PALE, socket);
  iris.position.z = 0.17;
  const pupil = M(new THREE.CylinderGeometry(0.15, 0.15, 0.03, 16), BLACK, socket);
  pupil.rotation.x = Math.PI / 2;
  pupil.position.z = 0.16;
  const slit = M(new THREE.OctahedronGeometry(0.07, 0), RED, socket);
  slit.scale.set(0.6, 2.4, 0.5);
  slit.position.z = 0.19;
  if (wear === 2) {
    for (const [x, y, r] of [[0.05, 0.08, 0.6], [-0.08, -0.05, -0.4]] as const) box(socket, BLACK, 0.016, 0.22, 0.01, x, y, 0.235).rotation.z = r;
  }
  glowSprite(socket, '#dfe8ff', 1.0, V(0, 0, 0.24), [0.85, 0.7, 0.45][wear] as number);
  glowSprite(socket, '#ff4f3a', 0.35, V(0, 0, 0.26), [0.8, 0.65, 0.4][wear] as number);
  // Rays round the socket, a crest on top.
  for (let i = 0; i < 10; i++) {
    if (wear === 2 && i === 3) continue;
    const a = (i / 10) * TAU + Math.PI / 10;
    const ray = M(new THREE.ConeGeometry(0.045, i % 2 ? 0.2 : 0.3, 4), TITAN_DK, socket);
    const rr = i % 2 ? 0.66 : 0.71;
    ray.position.set(Math.cos(a) * rr, Math.sin(a) * rr * 0.75, -0.02);
    ray.rotation.z = a - Math.PI / 2;
  }
  M(new THREE.ConeGeometry(0.06, 0.28, 4), TITAN_LT, socket).position.set(0, 0.68, -0.02);
  M(new THREE.OctahedronGeometry(0.045, 0), PALE, socket).position.set(0, 0.85, -0.02);

  if (wear > 0) {
    for (let i = 0; i < wear * 2 + 1; i++) crackRound(g, 0.46, 0.55 - wr() * 0.15, (i / (wear * 2 + 1)) * TAU + wr(), wear === 2 ? 0.3 : 0.2, wr);
    for (let i = 0; i < wear * 2; i++) {
      const a = wr() * TAU;
      crack(g, Math.sin(a) * 1.0, 0.142, Math.cos(a) * 1.0, a, 0.2, wr);
    }
    rubbleAround(g, wear === 2 ? 14 : 6, 1.1, wr, ['#3b3f47', '#2a2d33', '#555a63']);
  }
  const t = (n: number): void => {
    eye.rotation.y = Math.sin(n * 0.15) * 0.6;
  };
  return done(c, 2.55, t);
};

/* ---------- ruins ---------- */

/** What a destroyed foundry, arsenal, lightning rod, gate tower or Titan's Eye leaves, in its material. */
export const obraRuin = (type: string, mat: Era2Material = 2): Model => {
  const c = begin(undefined, 0.8, 211);
  const { g } = c;
  const rng = mulberry32(2110 + type.length * 13 + type.charCodeAt(0) + mat * 5);
  const wallM = wallOf(mat);
  const colors = rubbleOf(mat);
  const scorch = (r: number, x: number, z: number, sx: number, y = Y0): void => {
    const d = M(new THREE.CircleGeometry(r, 12), std('#1e1a16', { roughness: 1, bumpScale: 0 }), g);
    d.rotation.x = -Math.PI / 2;
    d.scale.x = sx;
    d.position.set(x, y + 0.004, z);
  };
  const ironBits = (n: number, r: number): void => {
    if (mat !== 3) return;
    for (let i = 0; i < n; i++) {
      const a = rng() * TAU;
      const d = r * (0.5 + rng() * 0.5);
      const p = box(g, IRON_PLATE, 0.12 + rng() * 0.06, 0.012, 0.08 + rng() * 0.05, Math.sin(a) * d, Y0 + 0.02, Math.cos(a) * d - 0.1, rng() * 3);
      p.rotation.x = (rng() - 0.5) * 0.6;
    }
  };
  let h: number;
  switch (type) {
    case 'foundry': {
      yard(g, mat);
      scorch(0.45, -0.1, -0.25, 1.4);
      // The kiln's stump, the stack across the yard, slag and a last glow in the runnel.
      M(new THREE.CylinderGeometry(0.3, 0.35, 0.24, 10), wallM, g).position.set(-0.3, Y0 + 0.12, -0.3);
      snapTop(g, -0.3, Y0 + 0.24, -0.3, 0.12, wallM, rng);
      ring(g, 0.33, 0.016, IRON_BAND, Y0 + 0.1, 4, 20).position.set(-0.3, Y0 + 0.1, -0.3);
      const stack = M(new THREE.CylinderGeometry(0.1, 0.12, 0.6, 8), wallM, g);
      stack.position.set(0.15, Y0 + 0.1, -0.15);
      stack.rotation.set(0, 0.6, Math.PI / 2);
      for (let i = 0; i < 6; i++) M(new THREE.DodecahedronGeometry(0.04 + rng() * 0.03, 0), i % 2 ? SLAG : ORE, g).position.set(-0.5 + rng() * 0.3, Y0 + 0.03, 0.1 + rng() * 0.25);
      box(g, FORGE, 0.03, 0.012, 0.2, -0.15, Y0 + 0.012, 0.2, 0.6);
      glowSprite(g, '#ff6a1a', 0.3, V(-0.15, Y0 + 0.05, 0.2), 0.35);
      for (let i = 0; i < 4; i++) box(g, DSTEEL, 0.15, 0.03, 0.045, 0.35 + rng() * 0.2, Y0 + 0.015, 0.1 + rng() * 0.2, rng() * 3);
      rubbleAround(g, 9, 0.55, rng, colors, 0, -0.15);
      splinters(g, 5, 0.5, rng, 0, -0.1);
      ironBits(4, 0.5);
      h = 0.36;
      break;
    }
    case 'arsenal': {
      yard(g, mat);
      scorch(0.5, 0, -0.3, 1.3);
      // Wall stubs, charred planks, bolts and stones thrown across the yard.
      for (let i = 0; i < 6; i++) {
        const sh = 0.05 + rng() * 0.18;
        box(g, mat === 1 ? CHARRED : wallM, 0.15, sh, 0.06, -0.42 + i * 0.17, Y0 + sh / 2, -0.17);
        const sb = 0.05 + rng() * 0.2;
        box(g, mat === 1 ? CHARRED : wallM, 0.15, sb, 0.06, -0.42 + i * 0.17, Y0 + sb / 2, -0.58);
      }
      for (let i = 0; i < 5; i++) {
        const p = box(g, i % 2 ? CHARRED : PLANK, 0.06, 0.02, 0.4 + rng() * 0.2, -0.35 + i * 0.17, Y0 + 0.03 + (i % 2) * 0.02, -0.38, 0.3 + rng() * 0.6);
        p.rotation.z = (rng() - 0.5) * 0.3;
      }
      for (let i = 0; i < 6; i++) {
        const b = G(g, -0.45 + rng() * 0.4, Y0 + 0.012, rng() * 0.35);
        b.rotation.y = rng() * 3;
        M(new THREE.CylinderGeometry(0.009, 0.009, 0.46, 4), i % 3 ? WOOD : CHARRED, b).rotation.x = Math.PI / 2;
      }
      for (let i = 0; i < 6; i++) M(new THREE.DodecahedronGeometry(0.065, 0), stone('#6d6559'), g).position.set(0.25 + rng() * 0.35, Y0 + 0.06, rng() * 0.35);
      rubbleAround(g, mat === 1 ? 4 : 9, 0.55, rng, colors, 0, -0.15);
      splinters(g, 7, 0.5, rng, 0, -0.1);
      ironBits(4, 0.5);
      h = 0.32;
      break;
    }
    case 'rod': {
      M(new THREE.CylinderGeometry(0.36, 0.4, 0.12, 8), mat === 1 ? PLINTH : wallM, g).position.y = 0.06;
      M(new THREE.CylinderGeometry(0.24, 0.29, 0.14, 8), mat === 1 ? PLINTH : wallM, g).position.y = 0.19;
      snapTop(g, 0, 0.26, 0, 0.18, mat === 1 ? PLINTH : wallM, rng);
      // The mast down across the ground, its coil and crown at the end.
      const mast = G(g, 0.05, 0.05, 0.05);
      mast.rotation.y = 0.7;
      limb(mast, V(0, 0, 0), V(1.0, 0, 0), 0.035, 0.024, IRON_BAND, 6);
      const coil = M(new THREE.CylinderGeometry(0.08, 0.08, 0.36, 10), COPPER, mast);
      coil.rotation.z = Math.PI / 2;
      coil.position.x = 0.55;
      ring(g, 0.39, 0.012, COPPER, 0.125, 4, 16);
      rubbleAround(g, 9, 0.45, rng, colors);
      h = 0.3;
      break;
    }
    case 'gatetower': {
      scorch(0.62, 0, 0, 1, 0);
      const st = mat === 1 ? stone('#77736c') : wallM;
      M(new THREE.CylinderGeometry(0.52, 0.58, 0.32, 10), st, g).position.y = 0.16;
      const core = M(new THREE.CircleGeometry(0.46, 10), stone('#34302b'), g);
      core.rotation.x = -Math.PI / 2;
      core.position.y = 0.323;
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * TAU + rng() * 0.5;
        const sh = 0.05 + rng() * 0.18;
        const m = box(g, st, 0.12 + rng() * 0.06, sh, 0.1, Math.sin(a) * 0.48, 0.32 + sh / 2 - 0.01, Math.cos(a) * 0.48, a);
        m.rotation.z = (rng() - 0.5) * 0.25;
      }
      // The ballista's stock thrown across the rubble.
      const beam = box(g, DWOOD, 0.08, 0.08, 0.6, 0.4, 0.12, 0.5, 0.8);
      beam.rotation.x = 0.25;
      rubbleAround(g, 14, 0.55, rng, colors);
      if (mat === 1) splinters(g, 8, 0.5, rng);
      ironBits(3, 0.5);
      h = 0.5;
      break;
    }
    default: {
      // The Titan's Eye: the dais cracked, the plinths broken, the socket fallen on its back and the iris dark.
      M(new THREE.CylinderGeometry(1.45, 1.55, 0.14, 12), TITAN, g).position.y = 0.07;
      M(new THREE.CylinderGeometry(0.8, 0.86, 0.14, 12), TITAN_LT, g).position.y = 0.21;
      for (let k = 0; k < 6; k++) {
        const a = (k * TAU) / 6;
        const pg = G(g, Math.sin(a) * EYE_RING, 0.14, Math.cos(a) * EYE_RING);
        box(pg, TITAN_DK, 0.36, 0.1, 0.36, 0, 0.05, 0);
        const sh = 0.08 + rng() * 0.22;
        M(new THREE.CylinderGeometry(0.13, 0.15, sh, 6), TITAN, pg).position.y = 0.1 + sh / 2;
        snapTop(pg, 0, 0.1 + sh, 0, 0.1, TITAN, rng);
      }
      M(new THREE.CylinderGeometry(0.42, 0.52, 0.22, 8), TITAN, g).position.y = 0.39;
      const socket = G(g, 0.55, 0.34, 0.35);
      socket.rotation.set(-Math.PI / 2 + 0.15, 0, 0.5);
      const frame = M(new THREE.TorusGeometry(0.5, 0.1, 6, 20), TITAN, socket);
      frame.scale.set(1, 0.72, 1);
      const ball = M(new THREE.SphereGeometry(0.4, 10, 6), TITAN_DK, socket);
      ball.scale.set(1, 0.7, 0.4);
      M(new THREE.TorusGeometry(0.2, 0.05, 4, 16), stone('#6f7480'), socket).position.z = 0.12;
      const shard = M(new THREE.OctahedronGeometry(0.06, 0), PALE, g);
      shard.position.set(-0.5, 0.2, 0.6);
      shard.rotation.z = 1.2;
      shard.scale.y = 2;
      glowSprite(g, '#dfe8ff', 0.3, V(-0.5, 0.22, 0.6), 0.35);
      rubbleAround(g, 20, 1.0, rng, ['#3b3f47', '#2a2d33', '#555a63']);
      h = 0.7;
    }
  }
  return done(c, h);
};

/** The pieces this module draws (and leaves ruins of). */
export const OBRA_TYPES = ['foundry', 'arsenal', 'rod', 'gatetower', 'titanEye'];
